import type { FormLocation } from '../utils/trackingUtils';
// Budget Email Service
// Handles sending budget form emails via Brevo API

import { emailConfig } from '../config/emailConfig';
import { processEmailTemplate } from './emailTemplateService';
import { fileListToAttachments } from '../utils/fileUtils';
import { LOGO_URL } from './logoConstants';

export interface EmailResult {
    success: boolean;
    messageId?: string;
    error?: string;
}

// Budget product interface for selected products
export interface BudgetProductData {
    id: string;
    productNamePt?: string;
    productNameEn?: string;
    productBrand?: string;
    productImage?: string;
    productCode?: string;
    categoryId?: number | string;
}

export interface BudgetFormData {
    name: string;
    surname: string;
    email: string;
    phone: string;
    carMake?: string;
    registrationYear?: string;
    kilometers?: string;
    licensePlate?: string;
    serviceType?: string;
    serviceDescription?: string;
    tireSize?: string;
    photos?: FileList | null;
    budgetProducts?: BudgetProductData[];
    tireMountingService?: boolean;
    form_location?: FormLocation;
}

// Service mapping
const servicesMap: Record<string, string> = {
    'revision': 'Revisão Roady',
    'workshop-services-pack': 'Pacote de Serviços de Oficina',
    'air-conditioning': 'Ar Condicionado',
    'washing': 'Lavagem',
    'headlight-restoration': 'Restauração de Faróis',
    'battery-service': 'Serviço de Bateria',
    'brake-service': 'Serviço de Travões',
    'oil-change': 'Mudança de Óleo e Filtros',
    'engine-repair': 'Reparação de Motor',
    'tire-services': 'Serviços de Pneus e Rodas',
    'diagnostic-test': 'Teste de Diagnóstico',
    'inspection': 'Inspeção',
    'general-mechanics': 'Mecânica Geral',
    'alignment': 'Alinhamento',
    'shock-absorbers': 'Amortecedores',
    'other': 'Outro Serviço'
};

/**
 * Get service name in Portuguese
 */
function getServiceName(serviceType: string | undefined): string {
    if (!serviceType) return 'Não especificado';
    return servicesMap[serviceType] || serviceType;
}

// Single email interface for batch sending
interface SingleEmailPayload {
    to: { email: string; name?: string };
    subject: string;
    htmlContent: string;
    sender?: { name: string; email: string };
    attachments?: Array<{ name: string; content: string }>;
}

// Batch email result
interface BatchEmailResult {
    success: boolean;
    results?: Array<{ success: boolean; messageId?: string; error?: string }>;
    summary?: { total: number; sent: number; failed: number };
    error?: string;
}

/**
 * Get reCAPTCHA token from the page
 */
function getRecaptchaToken(): string {
    try {
        if (typeof (window as any).grecaptcha !== 'undefined') {
            const token = (window as any).grecaptcha.getResponse();
            if (token) {
                return token;
            }
        }
        
        // Fallback: check hidden textarea
        const textarea = document.querySelector('#g-recaptcha-response') as HTMLTextAreaElement;
        if (textarea && textarea.value) {
            return textarea.value;
        }
    } catch (error) {
    }
    
    return '';
}

/**
 * Send batch emails via Firebase Cloud Function
 */
async function sendBatchEmailsViaBrevo(
    emails: SingleEmailPayload[],
    recaptchaToken?: string
): Promise<BatchEmailResult> {
    try {
        if (!emailConfig.CLOUD_FUNCTION_URL) {
            throw new Error('Cloud Function URL not configured. Please check Firebase Project ID.');
        }

        const token = recaptchaToken || getRecaptchaToken();
        if (!token) {
            throw new Error('reCAPTCHA token not found. Please complete the reCAPTCHA.');
        }

        const emailsWithSender = emails.map(email => ({
            ...email,
            sender: email.sender || {
                name: emailConfig.BREVO_FROM_NAME || 'Roady',
                email: emailConfig.BREVO_FROM_EMAIL || 'noreply@roady.pt'
            }
        }));

        const payload = {
            emails: emailsWithSender,
            recaptchaToken: token
        };


        const fetchOptions: RequestInit = {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        };

        let response: Response;
        try {
            response = await fetch(emailConfig.CLOUD_FUNCTION_URL, { ...fetchOptions, keepalive: true });
        } catch (err: any) {
            console.warn('keepalive fetch failed, retrying without keepalive:', err?.message);
            response = await fetch(emailConfig.CLOUD_FUNCTION_URL, fetchOptions);
        }

        const responseText = await response.text();

        if (!response.ok) {
            throw new Error(`Cloud Function error: ${response.status} - ${responseText}`);
        }

        let result;
        try {
            result = JSON.parse(responseText);
        } catch (e) {
            result = { success: true };
        }

        return {
            success: result.success,
            results: result.results,
            summary: result.summary
        };
    } catch (error: any) {
        return { success: false, error: error.message || 'Unknown error' };
    }
}

/**
 * Send email via Firebase Cloud Function
 * The Cloud Function handles Brevo API credentials securely
 */
async function sendEmailViaBrevo(
    to: { email: string; name?: string },
    subject: string,
    htmlContent: string,
    attachments?: Array<{ name: string; content: string }>
): Promise<EmailResult> {
    try {
        if (!emailConfig.CLOUD_FUNCTION_URL) {
            throw new Error('Cloud Function URL not configured. Please check Firebase Project ID.');
        }

        // Get reCAPTCHA token
        const recaptchaToken = getRecaptchaToken();
        if (!recaptchaToken) {
            throw new Error('reCAPTCHA token not found. Please complete the reCAPTCHA.');
        }

        const payload: any = {
            sender: {
                name: emailConfig.BREVO_FROM_NAME || 'Roady',
                email: emailConfig.BREVO_FROM_EMAIL || 'noreply@roady.pt'
            },
            to,
            subject,
            htmlContent,
            recaptchaToken
        };

        // Add attachments if provided
        if (attachments && attachments.length > 0) {
            payload.attachments = attachments;
        }


        const fetchOptions: RequestInit = {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        };

        let response: Response;
        try {
            response = await fetch(emailConfig.CLOUD_FUNCTION_URL, { ...fetchOptions, keepalive: true });
        } catch (err: any) {
            console.warn('keepalive fetch failed, retrying without keepalive:', err?.message);
            response = await fetch(emailConfig.CLOUD_FUNCTION_URL, fetchOptions);
        }

        const responseText = await response.text();

        if (!response.ok) {
            throw new Error(`Cloud Function error: ${response.status} - ${responseText}`);
        }

        let result;
        try {
            result = JSON.parse(responseText);
        } catch (e) {
            result = { messageId: 'sent' };
        }

        return { success: true, messageId: result.messageId };
    } catch (error: any) {
        return { success: false, error: error.message || 'Unknown error' };
    }
}

/**
 * Send budget notification email to admin
 */
export async function sendBudgetAdminEmail(
    formData: BudgetFormData,
    companyName: string = 'Roady Leiria'
): Promise<EmailResult> {
    try {

        const serviceName = getServiceName(formData.serviceType);
        const currentDate = new Date().toLocaleString('pt-PT', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });

        const hasVehicleInfo = !!(formData.carMake || formData.registrationYear || formData.licensePlate || formData.kilometers);
        const photosCount = formData.photos ? formData.photos.length : 0;

        // Prepare template variables
        const variables: Record<string, string> = {
            company_name: companyName,
            name: formData.name || '',
            surname: formData.surname || '',
            email: formData.email || '',
            phone: formData.phone || '',
            car_make: formData.carMake || '',
            registration_year: formData.registrationYear || '',
            kilometers: formData.kilometers || '',
            license_plate: formData.licensePlate || '',
            service_name: serviceName,
            service_description: formData.serviceDescription || '',
            tire_size: formData.tireSize || '',
            tire_size_style: formData.tireSize ? '' : 'display: none;',
            photos_count: photosCount.toString(),
            timestamp: currentDate,
            year: new Date().getFullYear().toString(),
            vehicle_style: hasVehicleInfo ? '' : 'display: none;',
            car_make_style: formData.carMake ? '' : 'display: none;',
            registration_year_style: formData.registrationYear ? '' : 'display: none;',
            license_plate_style: formData.licensePlate ? '' : 'display: none;',
            kilometers_style: formData.kilometers ? '' : 'display: none;',
            service_description_style: formData.serviceDescription ? '' : 'display: none;',
            photos_style: photosCount > 0 ? '' : 'display: none;',
            products_html: generateProductsHtml(formData.budgetProducts, formData.tireMountingService)
        };

        // Process admin template
        const htmlContent = await processEmailTemplate('budget-admin.html', variables);

        const subject = `🚗 Novo Pedido de Orçamento de ${formData.name} ${formData.surname}`;

        // Convert photos to base64 attachments
        let attachments: Array<{ name: string; content: string }> = [];
        if (formData.photos && formData.photos.length > 0) {
            attachments = await fileListToAttachments(formData.photos);
        }

        const result = await sendEmailViaBrevo(
            {
                email: emailConfig.BREVO_TO_EMAIL_BUDGET,
                name: `${companyName} Admin`
            },
            subject,
            htmlContent,
            attachments
        );

        if (result.success) {
        }

        return result;
    } catch (error: any) {
        return { success: false, error: error.message || 'Unknown error' };
    }
}

// Get company address helper
function getCompanyAddress(name: string): string {
    if (name.includes('Valongo')) return 'Tv. António Augusto Castro Paupério 36, Valongo';
    if (name.includes('Leiria')) return 'Rua da Baquelite 204, Leiria';
    if (name.includes('Oliveira')) return 'Av. D. Maria I - Lugar de Aldas, 3720-300 Oliveira de Azeméis';
    if (name.includes('São Domingos')) return 'Avenida Antero Brotas – Sítio do Esteval, 2870-122 São Domingos de Rana';
    if (name.includes('Montijo')) return 'Av. dos Pescadores 18, 2870-355 Montijo';
    return 'Portugal';
}

/**
 * Generate HTML for budget products section
 */
function generateProductsHtml(products: BudgetProductData[] | undefined, tireMountingService?: boolean): string {
    if (!products || products.length === 0) {
        return '';
    }

    // Check if any product is a tire (categoryId === 7)
    const hasTireProduct = products.some(p => {
        const catId = typeof p.categoryId === 'string' ? parseInt(p.categoryId, 10) : p.categoryId;
        return catId === 7;
    });

    let html = `
    <div style="margin: 20px 0; padding: 20px; background: #f8f9fa; border-radius: 8px; border-left: 4px solid #d90202;">
        <h3 style="margin: 0 0 15px 0; color: #d90202; font-size: 16px;">🛞 Produtos Selecionados (${products.length})</h3>
        <table style="width: 100%; border-collapse: collapse;">
    `;

    products.forEach((product, index) => {
        const productName = product.productNamePt || product.productNameEn || 'Produto';
        const productBrand = product.productBrand || '';
        const productCode = product.productCode || '';
        const productImage = product.productImage || '';

        html += `
            <tr style="border-bottom: 1px solid #e0e0e0; ${index === products.length - 1 && !(hasTireProduct && tireMountingService) ? 'border-bottom: none;' : ''}">
                <td style="padding: 12px 0; width: 70px; vertical-align: top;">
                    ${productImage 
                        ? `<img src="${productImage}" alt="${productName}" style="width: 60px; height: 60px; object-fit: contain; border-radius: 6px; background: #fff;" />`
                        : `<div style="width: 60px; height: 60px; background: #e0e0e0; border-radius: 6px; display: flex; align-items: center; justify-content: center; color: #999;">📦</div>`
                    }
                </td>
                <td style="padding: 12px 10px; vertical-align: top;">
                    <div style="font-weight: 600; color: #333; margin-bottom: 4px;">${productName}</div>
                    ${productBrand ? `<div style="font-size: 13px; color: #d90202; margin-bottom: 2px;">${productBrand}</div>` : ''}
                    ${productCode ? `<div style="font-size: 12px; color: #888;">REF: ${productCode}</div>` : ''}
                </td>
            </tr>
        `;
    });

    html += `
        </table>
    `;

    // Add tire mounting service indicator if selected
    if (hasTireProduct && tireMountingService) {
        html += `
        <div style="margin-top: 15px; padding: 12px 15px; background: linear-gradient(135deg, #2ecc71 0%, #27ae60 100%); border-radius: 6px; display: flex; align-items: center;">
            <span style="font-size: 20px; margin-right: 10px;">🔧</span>
            <div>
                <div style="color: #fff; font-weight: 600; font-size: 14px;">Serviço Adicional Solicitado:</div>
                <div style="color: #fff; font-size: 13px; margin-top: 2px;">✓ Montagem de Pneus, Válvulas e Equilibragem</div>
            </div>
        </div>
        `;
    }

    html += `
    </div>
    `;

    return html;
}

/**
 * Prepare budget admin email payload (for batch sending)
 */
async function prepareBudgetAdminEmail(
    formData: BudgetFormData,
    companyName: string = 'Roady'
): Promise<SingleEmailPayload | null> {
    try {
        const serviceName = getServiceName(formData.serviceType);
        const currentDate = new Date().toLocaleString('pt-PT', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });

        const hasVehicleInfo = !!(formData.carMake || formData.registrationYear || formData.licensePlate || formData.kilometers);
        const photosCount = formData.photos ? formData.photos.length : 0;
        const hasProducts = formData.budgetProducts && formData.budgetProducts.length > 0;

        // Generate products HTML (including tire mounting service if selected)
        const productsHtml = generateProductsHtml(formData.budgetProducts, formData.tireMountingService);

        const variables: Record<string, string> = {
            company_name: companyName,
            name: formData.name || '',
            surname: formData.surname || '',
            email: formData.email || '',
            phone: formData.phone || '',
            car_make: formData.carMake || '',
            registration_year: formData.registrationYear || '',
            kilometers: formData.kilometers || '',
            license_plate: formData.licensePlate || '',
            service_name: serviceName,
            service_description: formData.serviceDescription || '',
            tire_size: formData.tireSize || '',
            tire_size_style: formData.tireSize ? '' : 'display: none;',
            photos_count: photosCount.toString(),
            timestamp: currentDate,
            year: new Date().getFullYear().toString(),
            vehicle_style: hasVehicleInfo ? '' : 'display: none;',
            car_make_style: formData.carMake ? '' : 'display: none;',
            registration_year_style: formData.registrationYear ? '' : 'display: none;',
            license_plate_style: formData.licensePlate ? '' : 'display: none;',
            kilometers_style: formData.kilometers ? '' : 'display: none;',
            service_description_style: formData.serviceDescription ? '' : 'display: none;',
            photos_style: photosCount > 0 ? '' : 'display: none;',
            products_html: productsHtml,
            products_style: hasProducts ? '' : 'display: none;'
        };

        let htmlContent = await processEmailTemplate('budget-admin.html', variables);
        
        // If products HTML wasn't replaced by template (template doesn't have placeholder),
        // we need to inject it manually before the closing of the main content
        if (hasProducts && !htmlContent.includes('Produtos Selecionados')) {
            // Try to inject before closing table
            if (htmlContent.includes('</table>')) {
                const lastTableIndex = htmlContent.lastIndexOf('</table>');
                htmlContent = htmlContent.slice(0, lastTableIndex) + 
                    `<tr><td colspan="2" style="padding: 0;">${productsHtml}</td></tr>` +
                    htmlContent.slice(lastTableIndex);
            }
        }
        
        const subject = hasProducts 
            ? `🚗🛞 Novo Pedido de Orçamento de ${formData.name} ${formData.surname} (${formData.budgetProducts!.length} produto(s))`
            : `🚗 Novo Pedido de Orçamento de ${formData.name} ${formData.surname}`;

        // Convert photos to base64 attachments
        let attachments: Array<{ name: string; content: string }> = [];
        if (formData.photos && formData.photos.length > 0) {
            attachments = await fileListToAttachments(formData.photos);
        }

        return {
            to: { email: emailConfig.BREVO_TO_EMAIL_BUDGET, name: `${companyName} Admin` },
            subject,
            htmlContent,
            attachments: attachments.length > 0 ? attachments : undefined
        };
    } catch (error: any) {
        return null;
    }
}

/**
 * Prepare budget user email payload (for batch sending)
 */
async function prepareBudgetUserEmail(
    formData: BudgetFormData,
    companyName: string = 'Roady',
    language: string = 'pt'
): Promise<SingleEmailPayload | null> {
    try {
        if (!formData.email) {
            return null;
        }

        const serviceName = getServiceName(formData.serviceType);

        const variables: Record<string, string> = {
            company_name: companyName,
            name: formData.name || '',
            surname: formData.surname || '',
            email: formData.email || '',
            phone: formData.phone || '',
            car_make: formData.carMake || '',
            registration_year: formData.registrationYear || '',
            kilometers: formData.kilometers || '',
            license_plate: formData.licensePlate || '',
            service_name: serviceName,
            service_description: formData.serviceDescription || '',
            tire_size: formData.tireSize || '',
            tire_size_style: formData.tireSize ? '' : 'display: none;',
            year: new Date().getFullYear().toString(),
            car_make_style: formData.carMake ? '' : 'display: none;',
            registration_year_style: formData.registrationYear ? '' : 'display: none;',
            license_plate_style: formData.licensePlate ? '' : 'display: none;',
            kilometers_style: formData.kilometers ? '' : 'display: none;',
            service_description_style: formData.serviceDescription ? '' : 'display: none;',
            logo_url: LOGO_URL,
            budget_email: emailConfig.BREVO_TO_EMAIL_BUDGET || '',
            from_name: emailConfig.BREVO_FROM_NAME || companyName,
            company_address: getCompanyAddress(companyName)
        };

        const templateName = language === 'en'
            ? 'budget-user-en.html'
            : 'budget-user-pt.html';
        const htmlContent = await processEmailTemplate(templateName, variables);

        const subject = language === 'en'
            ? `Budget Request Confirmation - ${companyName}`
            : `Confirmação de Pedido de Orçamento - ${companyName}`;

        // Include the same photo attachments on the customer confirmation
        let attachments: Array<{ name: string; content: string }> = [];
        if (formData.photos && formData.photos.length > 0) {
            attachments = await fileListToAttachments(formData.photos);
        }

        return {
            to: { email: formData.email, name: `${formData.name} ${formData.surname}` },
            subject,
            htmlContent,
            attachments: attachments.length > 0 ? attachments : undefined
        };
    } catch (error: any) {
        return null;
    }
}

/**
 * Send both budget emails in a single batch (admin + user confirmation)
 */
export async function sendBudgetEmailsBatch(
    formData: BudgetFormData,
    companyName: string = 'Roady',
    language: string = 'pt',
    recaptchaToken?: string
): Promise<BatchEmailResult> {
    try {

        const emails: SingleEmailPayload[] = [];

        const adminEmail = await prepareBudgetAdminEmail(formData, companyName);
        if (adminEmail) {
            emails.push(adminEmail);
        }

        const userEmail = await prepareBudgetUserEmail(formData, companyName, language);
        if (userEmail) {
            emails.push(userEmail);
        }

        if (emails.length === 0) {
            throw new Error('No emails to send');
        }

        const result = await sendBatchEmailsViaBrevo(emails, recaptchaToken);

        if (result.success) {
        } else {
        }

        return result;
    } catch (error: any) {
        return { success: false, error: error.message || 'Unknown error' };
    }
}

/**
 * Send budget confirmation email to user
 */
export async function sendBudgetUserEmail(
    formData: BudgetFormData,
    companyName: string = 'Roady Leiria',
    language: string = 'pt'
): Promise<EmailResult> {
    try {
        if (!formData.email) {
            return { success: true }; // No email provided, skip
        }


        const serviceName = getServiceName(formData.serviceType);

        // Prepare template variables
        const variables: Record<string, string> = {
            company_name: companyName,
            name: formData.name || '',
            surname: formData.surname || '',
            email: formData.email || '',
            phone: formData.phone || '',
            car_make: formData.carMake || '',
            registration_year: formData.registrationYear || '',
            kilometers: formData.kilometers || '',
            license_plate: formData.licensePlate || '',
            service_name: serviceName,
            service_description: formData.serviceDescription || '',
            tire_size: formData.tireSize || '',
            tire_size_style: formData.tireSize ? '' : 'display: none;',
            year: new Date().getFullYear().toString(),
            car_make_style: formData.carMake ? '' : 'display: none;',
            registration_year_style: formData.registrationYear ? '' : 'display: none;',
            license_plate_style: formData.licensePlate ? '' : 'display: none;',
            kilometers_style: formData.kilometers ? '' : 'display: none;',
            service_description_style: formData.serviceDescription ? '' : 'display: none;',
            logo_url: LOGO_URL,
            budget_email: emailConfig.BREVO_TO_EMAIL_BUDGET || '',
            from_name: emailConfig.BREVO_FROM_NAME || companyName,
            company_address: getCompanyAddress(companyName)
        };

        // Process user template based on language
        const templateName = language === 'en' 
            ? 'budget-user-en.html' 
            : 'budget-user-pt.html';
        const htmlContent = await processEmailTemplate(templateName, variables);

        const subject = language === 'en'
            ? `Budget Request Confirmation - ${companyName}`
            : `Confirmação de Pedido de Orçamento - ${companyName}`;

        const result = await sendEmailViaBrevo(
            {
                email: formData.email,
                name: `${formData.name} ${formData.surname}`
            },
            subject,
            htmlContent
        );

        if (result.success) {
        }

        return result;
    } catch (error: any) {
        return { success: false, error: error.message || 'Unknown error' };
    }
}

